import React, { Fragment, memo, useCallback, useMemo, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { Button, Empty } from "antd";
import flatten from "lodash.flatten";
import xor from "lodash.xor";
import { useTranslation } from "react-i18next";

import {
  type ExerciseInWorkout,
  type SuperSet,
  type WorkoutExercisesType,
} from "@fitness-app/data-models/entities/TrainingProgram";
import { prepareMergedWorkoutExercises } from "@fitness-app/utils/src/programs/prepareMergedWorkoutExercises";

import { SortableItem } from "~/components/Dnd/SortableItem";
import ExerciseInProgramItem from "~/modules/TrainingPrograms/ProgramBuilder/WorkoutDay/components/ExerciseInProgramItem/ExerciseInProgramItem";
import RestGap from "~/modules/TrainingPrograms/ProgramBuilder/WorkoutDay/components/RestGap";
import WorkoutDayFooter from "~/modules/TrainingPrograms/ProgramBuilder/WorkoutDay/components/WorkoutDayFooter";
import { useWorkoutActionProviderContext } from "~/modules/TrainingPrograms/ProgramBuilder/WorkoutDay/providers/WorkoutActionProvider";
import { useProgramBuilderOptionalContext, useProgramIsTouched } from "~/shared/providers/ProgramBuilderProvider";

interface WorkoutExercisesProps {
  exercises: ExerciseInWorkout[];
  exercisesType: WorkoutExercisesType;
  workoutId: string;
  /** A later part of the workout has exercises, so the last one here is followed by a rest too. */
  restAfterLast?: boolean;
}

const WorkoutExercises = ({
  exercises,
  exercisesType,
  workoutId,
  restAfterLast = false,
}: WorkoutExercisesProps): React.ReactElement => {
  const { setNodeRef } = useDroppable({
    id: `PLACEHOLDER__${workoutId}__${exercisesType}`,
  });
  const { t } = useTranslation("workouts");

  const [selectedExercisesKeys, setSelectedExercisesKeys] = useState<string[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const {
    openExerciseForm,
    groupExercisesIntoSuperSet,
    splitExercisesFromSuperSet,
    deleteExerciseFromWorkout,
    handleRemoveSeriesFromExercise,
    handleAddSeriesToExercise,
    handleUpdateSuperSetSeries,
    updateExerciseInWorkout,
    toggleRecordingVideoFlag,
    updateRestAfterExercise,
    defaultRestBetweenExercises,
  } = useWorkoutActionProviderContext();
  const context = useProgramBuilderOptionalContext();

  const showRecordingVideoSwitch = context?.showRecordingVideoSwitch ?? false;

  const touched = useProgramIsTouched();
  const mergedExercises = useMemo(() => prepareMergedWorkoutExercises(exercises), [exercises]);
  const items = useMemo(() => mergedExercises.map((exe) => exe.id), [mergedExercises]);

  const getIndex = useCallback(
    (id: string) => {
      const index = items.indexOf(id);

      return index;
    },
    [items],
  );

  const onExerciseSelect = (exercise: ExerciseInWorkout | SuperSet) => {
    setSelectedRowIds((prev) => xor(prev, [exercise.id]));
    setSelectedExercisesKeys((prev) =>
      xor(prev, flatten(["mergedExercisesIds" in exercise ? exercise.mergedExercisesIds : exercise.id])),
    );
  };
  const groupInSuperSet = () => {
    groupExercisesIntoSuperSet(selectedExercisesKeys, exercisesType, workoutId);
    setSelectedRowIds([]);
    setSelectedExercisesKeys([]);
  };

  const splitSuperSet = (recordToSplit: SuperSet) => {
    if (recordToSplit?.mergedExercisesIds.length) {
      splitExercisesFromSuperSet(recordToSplit.mergedExercisesIds, exercisesType, workoutId);
    }
  };

  const toggleRecordingFlag = useCallback(
    (exerciseId: string, checked: boolean) => {
      if (toggleRecordingVideoFlag && showRecordingVideoSwitch) {
        return toggleRecordingVideoFlag(exerciseId, checked, exercisesType);
      }
      return undefined;
    },
    [exercisesType, showRecordingVideoSwitch, toggleRecordingVideoFlag],
  );

  return (
    <div>
      <div ref={setNodeRef}>
        {items.length === 0 && (
          <div>
            <Empty
              imageStyle={{ height: 30 }}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <p className="mb-5">{t("exerciseInProgram.emptyList")}</p>
                </div>
              }
            >
              <Button
                size="small"
                type="primary"
                disabled={touched}
                icon={<PlusOutlined />}
                onClick={() => openExerciseForm(exercisesType)}
                className="mb-5 self-center"
              >
                {t("workout.addExercise")}
              </Button>
            </Empty>
          </div>
        )}
        <SortableContext items={items}>
          {mergedExercises.map((item, index) => {
            // A superset keeps the rest after it on its last exercise; a new exercise goes after that one too.
            const lastExercise = item.isSuperSet ? item.mergedExercises.at(-1) : item;
            const isLast = index === mergedExercises.length - 1;
            return (
              <Fragment key={item.id}>
                <SortableItem
                  disabled={touched}
                  id={item.id}
                  index={index}
                  containerId={exercisesType}
                  getIndex={getIndex}
                  handle
                >
                  <ExerciseInProgramItem
                    touched={touched}
                    toggleRecordingVideoFlag={showRecordingVideoSwitch ? toggleRecordingFlag : undefined}
                    exerciseInWorkout={item}
                    position={index + 1}
                    onExerciseSelect={() => onExerciseSelect(item)}
                    isSelected={selectedRowIds.includes(item.id)}
                    splitSuperSet={splitSuperSet}
                    deleteExerciseFromWorkout={(id) => deleteExerciseFromWorkout(id, exercisesType)}
                    addSeriesToExercise={() => handleAddSeriesToExercise(item.id, exercisesType)}
                    removeSeriesFromExercise={(setNumber) =>
                      handleRemoveSeriesFromExercise(setNumber, item.id, exercisesType)
                    }
                    changeNumberOfSeries={(numberOfSeries, exercisesIds) =>
                      handleUpdateSuperSetSeries(numberOfSeries, exercisesIds, exercisesType)
                    }
                    updateExerciseInWorkout={(exercise) => updateExerciseInWorkout(exercise, exercisesType)}
                  />
                </SortableItem>
                {lastExercise && (!isLast || restAfterLast) ? (
                  <RestGap
                    override={lastExercise.restAfterExercise}
                    defaultRest={defaultRestBetweenExercises}
                    disabled={touched}
                    onChange={(value) => updateRestAfterExercise(lastExercise.id, value, exercisesType)}
                    onAdd={isLast ? undefined : () => openExerciseForm(exercisesType, lastExercise.id)}
                  />
                ) : null}
              </Fragment>
            );
          })}
        </SortableContext>
      </div>
      <WorkoutDayFooter
        exercisesType={exercisesType}
        visible={!!exercises.length}
        onMergeIntoSuperSet={selectedRowIds.length > 1 ? groupInSuperSet : undefined}
      />
    </div>
  );
};

export default memo(WorkoutExercises);
